import React, {
  useImperativeHandle, forwardRef, useEffect, useMemo
} from 'react';
import { PropTypes } from 'prop-types';
import {
  Container, Grid, FormControl, Radio, RadioGroup, FormControlLabel, FormLabel, Card, CardContent, Typography
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import FormTextField from '@seccon/core/components/FormTextField';
import FormDatePicker from '@seccon/core/components/FormDatePicker';
import FormDropDown from '@seccon/core/components/FormDropDown';
import FormMultipleSelect from '@seccon/core/components/FormMultipleSelect';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

const IncidentForm = forwardRef(({
  statusOfIncident, otherPersonnelInCurrentFacility, cognizantOffice, otherFacilities
}, ref) => {
  const {
    control, trigger, formState: { errors }, watch, setValue
  } = useFormContext({});
  const facility = watch('incident.facility');
  const employeeName = watch('foreignTravel.travelingPersonnel.id');

  const onValidate = async () => {
    const isValid = await trigger();
    if (isValid) {
      console.log('Validation successful');
    } else {
      console.log('Validation failed');
    }
    return isValid;
  };

  useImperativeHandle(ref, () => ({
    validate: async () => {
      const isValid = await onValidate();
      return isValid;
    }
  }));

  useEffect(() => {
    if (facility?.facilityName && facility?.facilityCode) {
      setValue(
        'incident.facilityNameCode',
        `${ facility.facilityName } - ${ facility.facilityCode }`
      );
    }
  }, [facility, setValue]);

  const employeesInvolvedOptions = useMemo(() => (
    otherPersonnelInCurrentFacility.map((person) => ({
      ...person,
      // The employee that was reported on should always be in the employees involved list
      locked: person.id === employeeName?.id
    }))
  ), [employeeName, otherPersonnelInCurrentFacility]);

  return (
    <Container>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <form>
          <Card variant='outlined' style={{ marginBottom: '1rem' }}>
            <CardContent>
              <Grid item xs={12} sx={{ mb: '15px' }}>
                <Typography variant='h5'>
                  Incident Information
                </Typography>
              </Grid>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <FormTextField
                    control={control}
                    formErrors={errors}
                    label='Incident Type'
                    formFieldId='incident.type'
                    defaultValue='Foreign Travel'
                    readonly
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormDropDown
                    control={control}
                    formErrors={errors}
                    label='Incident Status'
                    formFieldId='incident.status'
                    valueName='name'
                    options={statusOfIncident}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormTextField
                    control={control}
                    formErrors={errors}
                    label='Incident Facility'
                    formFieldId='incident.facilityNameCode'
                    defaultValue={`${ facility?.facilityName } - ${ facility?.facilityCode }`}
                    readonly
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormTextField
                    control={control}
                    formErrors={errors}
                    label='Description'
                    formFieldId='incident.description'
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormDatePicker
                    control={control}
                    formErrors={errors}
                    label='Incident Date '
                    formFieldId='incident.incidentDate'
                    views={['year', 'month', 'day']}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormMultipleSelect
                    control={control}
                    formErrors={errors}
                    label='Employees Involved '
                    formFieldId='incident.employeesInvolved'
                    options={employeesInvolvedOptions}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <Card variant='outlined' style={{ marginBottom: '1rem' }}>
            <CardContent>
              <Grid item xs={12} sx={{ mb: '15px' }}>
                <Typography variant='h5'>
                  Reporting Information
                </Typography>
              </Grid>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <FormDatePicker
                    control={control}
                    formErrors={errors}
                    label='Initial Report Date '
                    formFieldId='incident.initialReportDate'
                    views={['year', 'month', 'day']}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormTextField
                    control={control}
                    formErrors={errors}
                    label='Reported On'
                    formFieldId='foreignTravel.travelingPersonnel.fullName'
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormDatePicker
                    control={control}
                    formErrors={errors}
                    label='Final Report Date '
                    formFieldId='incident.finalReportDate'
                    views={['year', 'month', 'day']}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name='incident.compromise'
                    control={control}
                    render={({ field }) => (
                      <FormControl>
                        <FormLabel>Are you Compromised? *</FormLabel>
                        <RadioGroup
                          row
                          value={field.value ?? 'n/a'}
                          onChange={(e) => {
                            field.onChange(e);
                          }}
                        >
                          <FormControlLabel value='yes' control={<Radio />} label='Yes' />
                          <FormControlLabel value='no' control={<Radio />} label='No' />
                          <FormControlLabel value='n/a' control={<Radio />} label='Not Specified' />
                        </RadioGroup>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormTextField
                    control={control}
                    formErrors={errors}
                    label='Subjects Involved'
                    formFieldId='incident.involvedSubjects'
                    multiline
                    rows={3}
                    helperText='Enter the name, contact information, and descriptions of the subjects involved with this incident'
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <Card variant='outlined' style={{ marginBottom: '1rem' }}>
            <CardContent>
              <Grid item xs={12} sx={{ mb: '15px' }}>
                <Typography variant='h5'>
                  Responsible Agent
                </Typography>
              </Grid>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <FormDropDown
                    control={control}
                    formErrors={errors}
                    label='Cognizant Office'
                    formFieldId='incident.cognizantSecurityAuthority'
                    readonly
                    valueName='office'
                    options={cognizantOffice}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name='incident.cognizantOfficeNotified'
                    control={control}
                    render={({ field }) => (
                      <FormControl>
                        <FormLabel>Cognizant Office Is Notified? *</FormLabel>
                        <RadioGroup
                          row
                          value={field.value ?? 'n/a'}
                          onChange={(e) => {
                            field.onChange(e);
                          }}
                        >
                          <FormControlLabel value='yes' control={<Radio />} label='Yes' />
                          <FormControlLabel value='no' control={<Radio />} label='No' />
                          <FormControlLabel value='n/a' control={<Radio />} label='Not Specified' />
                        </RadioGroup>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormTextField
                    control={control}
                    formErrors={errors}
                    label='Cognizant Rep Name '
                    formFieldId='incident.cognizantRepresentative'
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormTextField
                    control={control}
                    formErrors={errors}
                    label='Employee / Visitor '
                    formFieldId='incident.employeeVisitor'
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormTextField
                    control={control}
                    formErrors={errors}
                    label='Findings '
                    multiline
                    formFieldId='incident.findings'
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormMultipleSelect
                    control={control}
                    formErrors={errors}
                    label='Other Facilities '
                    formFieldId='incident.otherFacilities'
                    options={otherFacilities}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormMultipleSelect
                    control={control}
                    formErrors={errors}
                    label='Notify List '
                    formFieldId='incident.notifyPersonnel'
                    options={otherPersonnelInCurrentFacility}
                  />
                </Grid>
              </Grid>

            </CardContent>
          </Card>
        </form>
      </LocalizationProvider>
    </Container>
  );
});

IncidentForm.propTypes = {
  statusOfIncident: PropTypes.arrayOf(PropTypes.object).isRequired,
  otherPersonnelInCurrentFacility: PropTypes.arrayOf(PropTypes.object).isRequired,
  cognizantOffice: PropTypes.arrayOf(PropTypes.object).isRequired,
  otherFacilities: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default IncidentForm;
